import { useEffect, useState } from 'react';
import { Instagram, Music, ShoppingBag } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { fetchList } from '@/lib/api';
import { SpotifyIcon, AppleMusicIcon } from '@/components/BrandIcons';

function ArtistModal({ artist, onClose }) {
  const [shopUrl, setShopUrl] = useState('');

  useEffect(() => {
    fetchList('/settings').then((s) => setShopUrl(s?.shop_url || '')).catch(() => {});
  }, []);

  useEffect(() => {
    if (!artist) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [artist, onClose]);

  return (
    <AnimatePresence>
      {artist && (
        <motion.div
          data-testid="artist-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-brayo-charcoal/40 p-5 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.94, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: 24, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-h-[92vh] w-full max-w-md overflow-y-auto border border-brayo-line bg-brayo-paper p-8 text-center shadow-[0_40px_90px_-40px_rgba(22,22,22,0.4)] md:p-10"
          >
            <button
              data-testid="artist-modal-close"
              onClick={onClose}
              aria-label="Close artist card"
              className="meta-label absolute right-4 top-4 flex h-11 w-11 items-center justify-center border border-brayo-line text-brayo-dim transition-colors hover:border-brayo-blue hover:text-brayo-blue"
            >
              ✕
            </button>

            <div className="mx-auto aspect-square w-36 overflow-hidden rounded-full bg-brayo-panel md:w-44">
              <img src={artist.image_url} alt={artist.name} className="h-full w-full object-cover" />
            </div>
            <p className="meta-label mt-6 text-brayo-blue">{artist.genre}</p>
            <h2 className="display-title mt-2 text-3xl text-brayo-charcoal md:text-4xl">{artist.name}</h2>
            {artist.bio && (
              <p className="mt-4 max-h-32 overflow-y-auto text-sm leading-relaxed text-brayo-mute">{artist.bio}</p>
            )}

            <div data-testid="artist-modal-links" className="mt-7 flex flex-wrap items-center justify-center gap-3">
              {artist.instagram && (
                <a data-testid="artist-modal-instagram" href={artist.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" title="Instagram" className="flex h-12 w-12 items-center justify-center rounded-full border border-brayo-line text-[#E1306C] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#E1306C] hover:bg-[#E1306C] hover:text-white">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {artist.apple_url && (
                <a data-testid="artist-modal-apple" href={artist.apple_url} target="_blank" rel="noopener noreferrer" aria-label="Apple Music" title="Apple Music" className="flex h-12 w-12 items-center justify-center rounded-full border border-brayo-line text-[#FA243C] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#FA243C] hover:bg-[#FA243C] hover:text-white">
                  <AppleMusicIcon className="h-5 w-5" />
                </a>
              )}
              {artist.spotify && (
                <a data-testid="artist-modal-spotify" href={artist.spotify} target="_blank" rel="noopener noreferrer" aria-label="Spotify" title="Spotify" className="flex h-12 w-12 items-center justify-center rounded-full border border-brayo-line text-[#1DB954] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#1DB954] hover:bg-[#1DB954] hover:text-white">
                  <SpotifyIcon className="h-5 w-5" />
                </a>
              )}
              {shopUrl && (
                <a data-testid="artist-modal-shop" href={shopUrl} target="_blank" rel="noopener noreferrer" aria-label="Shop" title="Shop" className="flex h-12 w-12 items-center justify-center rounded-full border border-brayo-blue bg-brayo-blue text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-brayo-charcoal hover:bg-brayo-charcoal">
                  <ShoppingBag className="h-5 w-5" />
                </a>
              )}
            </div>
            <p className="meta-label mt-5 text-brayo-dim">
              {[artist.instagram && 'Instagram', artist.apple_url && 'Apple Music', artist.spotify && 'Spotify', shopUrl && 'Shop'].filter(Boolean).join(' · ')}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function FeaturedArtist({ artist, onSelect }) {
  return (
    <button
      data-testid={`artist-featured-${artist.slug}`}
      onClick={() => onSelect(artist)}
      className="group flex cursor-pointer flex-col items-center text-center"
    >
      <div className="aspect-square w-full max-w-[240px] overflow-hidden rounded-full bg-brayo-panel md:max-w-[280px]">
        <img
          src={artist.image_url}
          alt={artist.name}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
        />
      </div>
      <h2 className="mt-6 text-xl font-bold text-brayo-charcoal transition-colors group-hover:text-brayo-blue md:text-2xl">
        {artist.name}
      </h2>
      {artist.genre && <p className="meta-label mt-2 text-brayo-dim">{artist.genre}</p>}
    </button>
  );
}

export function RosterRow({ artist, index, onSelect }) {
  return (
    <button
      onClick={() => onSelect(artist)}
      data-testid={`roster-row-${artist.slug}`}
      className="group flex w-full cursor-pointer items-center gap-5 border-b border-brayo-line py-4 text-left"
    >
      <span className="h-14 w-14 shrink-0 overflow-hidden rounded-full bg-brayo-panel md:h-16 md:w-16">
        <img
          src={artist.image_url}
          alt={artist.name}
          loading={index < 6 ? 'eager' : 'lazy'}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.08]"
        />
      </span>
      <span className="flex-1">
        <span className="block text-base font-semibold text-brayo-charcoal transition-colors group-hover:text-brayo-blue md:text-lg">
          {artist.name}
        </span>
        {artist.genre && <span className="meta-label mt-1 block text-brayo-dim">{artist.genre}</span>}
      </span>
      <svg className="h-4 w-4 text-brayo-blue opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </button>
  );
}

export function RosterBlock({ artists }) {
  const [selected, setSelected] = useState(null);
  const featured = artists.filter((a) => a.featured).slice(0, 2);
  const roster = [...artists].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <>
      {featured.length > 0 && (
        <div data-testid="artists-featured" className="mb-14 grid grid-cols-2 gap-8 md:mb-16 md:gap-16">
          {featured.map((a) => (
            <div key={a.id} className="flex justify-center">
              <FeaturedArtist artist={a} onSelect={setSelected} />
            </div>
          ))}
        </div>
      )}
      {roster.length > 0 ? (
        <div className="grid gap-x-10 sm:grid-cols-2 lg:grid-cols-3">
          {roster.map((a, i) => (
            <RosterRow key={a.id} artist={a} index={i} onSelect={setSelected} />
          ))}
        </div>
      ) : (
        <p className="meta-label text-brayo-dim">Roster announcements coming soon.</p>
      )}
      <ArtistModal artist={selected} onClose={() => setSelected(null)} />
    </>
  );
}